import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { X, Link2, Unlink } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { EstimateCombobox, type EstimatePick } from '@/components/EstimateCombobox';
import { invalidateEstimatePoLinkCaches } from '@/lib/linkCache';

// ─── LinkEstimateToPoModal ──────────────────────────────────────────────────────
// Link or unlink a purchase order to a source estimate. Used from the PO detail
// page (and reusable from the PO list). Confirming PATCHes the PO's estimate-link;
// when already linked, an Unlink action clears it.

interface EstimateLinkResponse {
  data: {
    id: number;
    source_estimate_id: number | null;
    source_estimate_number: string | null;
  };
}

export interface LinkEstimateToPoModalProps {
  poId: number;
  /** The PO's current source estimate, or null when unlinked. */
  currentEstimate: { id: number; number: string } | null;
  onClose: () => void;
  onLinked: (r: {
    source_estimate_id: number | null;
    source_estimate_number: string | null;
  }) => void;
}

export function LinkEstimateToPoModal({
  poId,
  currentEstimate,
  onClose,
  onLinked,
}: LinkEstimateToPoModalProps) {
  const queryClient = useQueryClient();
  const [picked, setPicked] = useState<EstimatePick | null>(null);
  const [loading, setLoading] = useState<'link' | 'unlink' | null>(null);
  const [error, setError] = useState('');

  async function patch(sourceEstimateId: number | null, mode: 'link' | 'unlink') {
    setError('');
    setLoading(mode);
    try {
      const res = await apiFetch<EstimateLinkResponse>(
        `/api/purchase-orders/${poId}/estimate-link`,
        { method: 'PATCH', body: JSON.stringify({ source_estimate_id: sourceEstimateId }) },
      );
      toast.success(sourceEstimateId === null ? 'Estimate unlinked' : 'Estimate linked');
      invalidateEstimatePoLinkCaches(queryClient);
      onLinked({
        source_estimate_id: res.data.source_estimate_id,
        source_estimate_number: res.data.source_estimate_number,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setLoading(null);
    }
  }

  const busy = loading !== null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg border bg-background shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-base font-semibold">
            {currentEstimate ? 'Change linked estimate' : 'Link estimate'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          {currentEstimate && (
            <p className="text-sm text-muted-foreground">
              Currently linked to{' '}
              <span className="font-mono font-medium text-foreground">
                {currentEstimate.number}
              </span>
              .
            </p>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="link-estimate-search">
              {currentEstimate ? 'Link to a different estimate' : 'Estimate'}
            </Label>
            <EstimateCombobox value={picked?.id ?? null} onChange={setPicked} disabled={busy} />
            <p className="text-xs text-muted-foreground">
              Search by estimate number or customer name.
            </p>
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex items-center justify-between gap-2 pt-2">
            <div>
              {currentEstimate && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => patch(null, 'unlink')}
                  disabled={busy}
                >
                  <Unlink className="h-3.5 w-3.5 mr-1.5" />
                  {loading === 'unlink' ? 'Unlinking…' : 'Unlink'}
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => picked && patch(picked.id, 'link')}
                disabled={busy || !picked}
              >
                <Link2 className="h-3.5 w-3.5 mr-1.5" />
                {loading === 'link' ? 'Linking…' : currentEstimate ? 'Change link' : 'Link'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
